'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import { useEffect, useRef } from 'react';
import { useStore } from '@/providers/app-store-provider';
import { ZoomDisablerType } from '@/store/slices/globalSlice';

/**
 * 移动端缩放禁用组件
 * 通过JavaScript事件监听来强制禁用所有缩放手势
 * 注意：viewport设置已移至layout.tsx的generateViewport()函数中统一管理
 */
export default function ZoomDisabler({ deviceType }: { deviceType: EPlatform }) {
  const isWeb = deviceType === EPlatform.WEB;
  const zoomDisablerType = useStore((state) => state.zoomDisablerType);
  const zoomDisablerTypeRef = useRef(zoomDisablerType);

  useEffect(() => {
    zoomDisablerTypeRef.current = zoomDisablerType;
  }, [zoomDisablerType]);

  useEffect(() => {
    if (isWeb) return;

    const shouldEnabled = (key: keyof ZoomDisablerType) => {
      // 默认禁用
      if (zoomDisablerTypeRef.current[key] == null) return false;
      return !zoomDisablerTypeRef.current[key];
    };

    // 禁用双指缩放的函数
    const disableZoom = (e: TouchEvent) => {
      if (shouldEnabled('zoom')) return;
      // 如果是多点触控（双指），阻止默认行为
      if (e.touches.length > 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 禁用键盘缩放的函数（Ctrl + / Ctrl -）
    const disableKeyboardZoom = (e: KeyboardEvent) => {
      if (shouldEnabled('keyboardZoom')) return;
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 禁用鼠标滚轮缩放
    const disableWheelZoom = (e: WheelEvent) => {
      if (shouldEnabled('wheelZoom')) return;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 记录最后一次触摸时间，用于检测双击
    let lastTouchEnd = 0;
    const disableDoubleTapZoom = (e: TouchEvent) => {
      if (shouldEnabled('doubleTapZoom')) return;
      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
        e.stopPropagation();
      }
      lastTouchEnd = now;
    };

    // 添加事件监听器
    document.addEventListener('touchstart', disableZoom, { passive: false });
    document.addEventListener('touchmove', disableZoom, { passive: false });
    document.addEventListener('touchend', disableDoubleTapZoom, { passive: false });
    document.addEventListener('keydown', disableKeyboardZoom, { passive: false });
    document.addEventListener('wheel', disableWheelZoom, { passive: false });

    // 阻止contextmenu（长按菜单）可能触发的缩放
    const disableContextMenu = (e: Event) => {
      if (shouldEnabled('contextMenu')) return;
      // 在某些情况下，长按可能会触发缩放相关的功能
      if (
        e.target &&
        (e.target as Element).tagName !== 'INPUT' &&
        (e.target as Element).tagName !== 'TEXTAREA'
      ) {
        // 允许在输入框中使用右键菜单
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', disableContextMenu);

    // viewport设置已移至layout.tsx的generateViewport()函数中统一管理
    // 这里专注于事件监听的缩放禁用功能

    // 清理函数
    return () => {
      document.removeEventListener('touchstart', disableZoom);
      document.removeEventListener('touchmove', disableZoom);
      document.removeEventListener('touchend', disableDoubleTapZoom);
      document.removeEventListener('keydown', disableKeyboardZoom);
      document.removeEventListener('wheel', disableWheelZoom);
      document.removeEventListener('contextmenu', disableContextMenu);
    };
  }, []);

  return null; // 这个组件不渲染任何内容
}
